import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, Search } from "lucide-react";
import { listAll, ref as storageRef } from "firebase/storage";
import { storage } from "../../../helpers/firebaseConfig";
import { fetchJsonFromStorage } from "../../../helpers/modelBuilder/mbStorageUtils";
import { resolveTemporalGeometry } from "../../../helpers/modelBuilder/temporalNetwork";
import { activeTemporalSegments, normalizeTemporalSegments, temporalBounds, temporalMagnitudeRanges } from "../../../helpers/modelBuilder/temporalPresentation";
import { boxWhiskerFieldKeys, downloadPresentationCsv, xyChartFieldKeys } from "../../../helpers/modelBuilder/agentPresentationCsv";
import { isDrawableBinding } from "../../../helpers/modelBuilder/entityBinding";
import AgentBoxWhiskerChart from "./charts/AgentBoxWhiskerChart";
import AgentXYChart from "./charts/AgentXYChart";
import AgentTemporalTimeline from "./charts/AgentTemporalTimeline";
import LiveAnalysisWorkspace, { TERMINAL as TERMINAL_JOB_STATUSES, analysisJobUpdatedAt } from "./LiveAnalysisWorkspace";
import ScrollJumpButton from "./ScrollJumpButton";
import ColourBar from "../ColourBar";
import "./AgentResultsWorkspace.css";

const DEFAULT_MAP_COLORS = ["#22c55e", "#facc15", "#ef4444"];
const EMPTY_NETWORK = Object.freeze({});
// Shared so that "nothing selected" keeps the same identity between renders; a fresh Set here would
// re-run the overlay effect on every render.
const EMPTY_SELECTION = new Set();

const numericMetricKeys = (columns, rows, idKey) => columns
  .filter((column) => column.key !== idKey && column.type !== "entity" && !/^(rank|index|row|#)$/i.test(column.key))
  .map((column) => column.key)
  .filter((key) => rows.some((row) => row?.[key] !== "" && row?.[key] != null && Number.isFinite(Number(row[key]))));

const mapRanges = (rows, metricKey, colors, customBoundaries = null) => {
  const values = rows.map((row) => Number(row?.[metricKey])).filter(Number.isFinite).sort((a, b) => a - b);
  if (!values.length) return [];
  const at = (fraction) => values[Math.min(values.length - 1, Math.floor((values.length - 1) * fraction))];
  const boundaries = customBoundaries?.length === colors.length - 1
    ? [...customBoundaries, values[values.length - 1]]
    : colors.map((_, index) => at((index + 1) / colors.length));
  let minimum = values[0];
  return boundaries.map((maximum, index) => {
    const range = { min: minimum, max: maximum, color: colors[index] };
    minimum = maximum;
    return range;
  });
};

const formatCell = (value, type) => {
  if (value == null) return "—";
  if (type === "percent" && Number.isFinite(Number(value))) return `${Math.round(Number(value) * 100)}%`;
  if (type === "number" && Number.isFinite(Number(value))) return Number(value).toLocaleString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const normalizeColumns = (presentation, rows) => {
  if (Array.isArray(presentation?.columns) && presentation.columns.length) return presentation.columns;
  return Object.keys(rows?.[0] || {}).map((key) => ({ key, label: key.replaceAll("_", " "), type: "text" }));
};

const MAX_RESULTS_PERCENT = 78;
const EMPTY_HAZARD_TIMES = [];

function hazardPolygonsFromGeojson(geojson) {
  const features = Array.isArray(geojson?.features) ? geojson.features : [];
  const hazardIds = [...new Set(features.map((feature, index) => String(feature?.properties?.hazard_id ?? feature?.id ?? `feature-${index}`)))];
  return features.flatMap((feature, featureIndex) => {
    const geometry = feature?.geometry || {};
    const shapes = geometry.type === "Polygon" ? [geometry.coordinates]
      : geometry.type === "MultiPolygon" ? geometry.coordinates : [];
    const hazardId = String(feature?.properties?.hazard_id ?? feature?.id ?? `feature-${featureIndex}`);
    const layerIndex = hazardIds.indexOf(hazardId);
    return (Array.isArray(shapes) ? shapes : []).map((shape, shapeIndex) => ({
      id: `${featureIndex}-${shapeIndex}`,
      layerIndex,
      paths: (Array.isArray(shape?.[0]) ? shape[0] : [])
        .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
        .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)),
    })).filter((polygon) => polygon.paths.length >= 3);
  });
}

export default function AgentResultsWorkspace({
  presentations = [],
  presentationsHydrated = false,
  presentationScopeKey = "",
  latestQuestionAt = null,
  requestedActiveId = "",
  activationKey = 0,
  focusedMessageId = "",
  onFocusMessage,
  children,
  onActivate,
  selectedIdsByType = {},
  onSetBoundSelection,
  onToggleBoundEntity,
  onZoomBoundEntities,
  onMapOverlayChange,
  onSelectTemporalEntity,
  analysisJobs = [],
  requestedAnalysisJobId = "",
  requestedAnalysisJob = null,
  requestedAnalysisTab = "",
  analysisActivationKey = 0,
  closedAnalysisJobId = "",
  onClose,
  onRequireMap,
  showResultsDock = true,
  dismissActivationKey = 0,
  returnToSimulationResults = false,
  onReturnToSimulationResults,
}) {
  const shellRef = useRef(null);
  const mapRef = useRef(null);
  const handledReadyRef = useRef(new Set());
  const hydratedPresentationScopeRef = useRef("");
  const handledAnalysisRequestRef = useRef("");
  const handledPresentationRequestRef = useRef("");
  const autoOpenedAnalysisJobsRef = useRef(new Set());
  const [selectedAt, setSelectedAt] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [open, setOpen] = useState(false);
  const [jumpTarget, setJumpTarget] = useState(null);
  const [resultsPercent, setResultsPercent] = useState(MAX_RESULTS_PERCENT);
  const [presentation, setPresentation] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [activeAnalysisJobId, setActiveAnalysisJobId] = useState("");
  const [mapMetric, setMapMetric] = useState("");
  const [mapColors, setMapColors] = useState(DEFAULT_MAP_COLORS);
  const [mapBoundaries, setMapBoundaries] = useState(null);
  const [temporalTime, setTemporalTime] = useState(0);
  const [showTemporalHazard, setShowTemporalHazard] = useState(false);
  const [hazardSnapshotIndex, setHazardSnapshotIndex] = useState({ resultId: "", status: "idle", times: [] });
  const [, setTemporalHazardCacheVersion] = useState(0);
  const temporalHazardCacheRef = useRef(new Map());
  const [temporalGeometry, setTemporalGeometry] = useState({ presentationId: "", links: [], nodes: [], missingGeometry: 0 });
  const [temporalColors, setTemporalColors] = useState(DEFAULT_MAP_COLORS);
  const [temporalBoundaries, setTemporalBoundaries] = useState(null);

  useEffect(() => {
    if (showResultsDock) return;
    setOpen(false);
    setActiveId("");
    setActiveAnalysisJobId("");
  }, [showResultsDock]);

  useEffect(() => {
    if (!dismissActivationKey) return;
    setOpen(false);
    setActiveId("");
    setActiveAnalysisJobId("");
  }, [dismissActivationKey]);

  const allPresentations = presentations;
  const activePresentation = allPresentations.find((item) => item.id === activeId);
  const keepSelectedResult = open && !!activeId && !activeAnalysisJobId
    && Number(latestQuestionAt) > 0 && selectedAt > Number(latestQuestionAt);
  const availableAnalysisJobs = useMemo(() => requestedAnalysisJob?.id && !analysisJobs.some((job) => job.id === requestedAnalysisJob.id)
    ? [...analysisJobs, requestedAnalysisJob] : analysisJobs, [analysisJobs, requestedAnalysisJob]);
  const latestAnalysisJob = useMemo(() => [...availableAnalysisJobs]
    .sort((a, b) => analysisJobUpdatedAt(b) - analysisJobUpdatedAt(a))[0] || null, [availableAnalysisJobs]);
  const activeAnalysisJob = availableAnalysisJobs.find((job) => job.id === activeAnalysisJobId) || null;
  // Firestore rebuilds the presentations array whenever any presentation changes.
  // Keep the active manifest stable unless that manifest's own contents change,
  // so a new chat turn cannot make the previous visualization flash/reload.
  const activePresentationFingerprint = useMemo(() => JSON.stringify(activePresentation || null), [activePresentation]);
  const activePresentationSnapshot = useMemo(
    () => JSON.parse(activePresentationFingerprint),
    [activePresentationFingerprint]
  );
  const activePresentationDataKey = useMemo(() => JSON.stringify({
    id: activePresentation?.id,
    storagePath: activePresentation?.storagePath || activePresentation?.data?.storagePath,
    rows: activePresentation?.rows,
    segments: activePresentation?.segments,
  }), [activePresentation]);
  const contextMessageId = focusedMessageId || activePresentation?.messageId || "";
  // The analysis behind the result in view, so a past answer reopens with its own code and
  // methods. A running job keeps its own chip alongside it.
  const contextAnalysisJob = useMemo(() => {
    if (!contextMessageId) return null;
    return [...availableAnalysisJobs]
      .filter((job) => String(job.presentedMessageId || "") === String(contextMessageId))
      .sort((a, b) => analysisJobUpdatedAt(b) - analysisJobUpdatedAt(a))[0] || null;
  }, [availableAnalysisJobs, contextMessageId]);
  const liveAnalysisJob = latestAnalysisJob && (!TERMINAL_JOB_STATUSES.has(latestAnalysisJob.status) || !contextMessageId)
    ? latestAnalysisJob : null;
  const analysisChips = [liveAnalysisJob, contextAnalysisJob]
    .filter((job, index, jobs) => job && jobs.findIndex((other) => other?.id === job.id) === index);
  const visiblePresentations = useMemo(() => {
    if (!presentations.length) return [];
    if (!contextMessageId) {
      const latest = presentations[presentations.length - 1];
      if (!latest) return [];
      return latest.messageId
        ? presentations.filter((item) => item.messageId === latest.messageId)
        : [latest];
    }
    return presentations.filter((item) => String(item.messageId || "") === String(contextMessageId));
  }, [presentations, contextMessageId]);

  const activate = useCallback((id, shouldOpen = true, manual = false) => {
    if (!showResultsDock) return;
    const wanted = String(id || "");
    if (!wanted) return;
    const item = allPresentations.find((candidate) => candidate.id === wanted);
    if (item && (item.kind === "map" || item.kind === "timeline_map" || isDrawableBinding(item.entityBinding))) {
      onRequireMap?.();
    }
    if (manual) setSelectedAt(Date.now());
    setActiveId(wanted);
    setActiveAnalysisJobId("");
    setOpen(shouldOpen && item?.kind !== "map");
    if (shouldOpen && ["table", "box_whisker", "chart", "timeline_map"].includes(item?.kind)) {
      setResultsPercent(MAX_RESULTS_PERCENT);
    }
    if (item?.messageId) onFocusMessage?.(item.messageId);
    setHistoryOpen(false);
  }, [allPresentations, onFocusMessage, onRequireMap, showResultsDock]);

  const openAnalysis = useCallback((jobId) => {
    if (!showResultsDock) return;
    const wanted = String(jobId || "");
    if (!wanted) return;
    setActiveAnalysisJobId(wanted);
    setOpen(true);
    setResultsPercent(MAX_RESULTS_PERCENT);
    setHistoryOpen(false);
  }, [showResultsDock]);

  useEffect(() => {
    const activeJob = [...availableAnalysisJobs]
      .filter((job) => ["queued", "running", "waiting_for_approval", "waiting_for_user"].includes(job.status))
      .sort((a, b) => analysisJobUpdatedAt(b) - analysisJobUpdatedAt(a))[0];
    if (!activeJob || activeJob.id === closedAnalysisJobId || autoOpenedAnalysisJobsRef.current.has(activeJob.id)) return;
    if (keepSelectedResult) return;
    autoOpenedAnalysisJobsRef.current.add(activeJob.id);
    openAnalysis(activeJob.id);
  }, [availableAnalysisJobs, openAnalysis, keepSelectedResult, closedAnalysisJobId]);

  useEffect(() => {
    const requestKey = `${requestedAnalysisJobId}:${analysisActivationKey}`;
    if (!requestedAnalysisJobId || handledAnalysisRequestRef.current === requestKey) return;
    if (!availableAnalysisJobs.some((job) => job.id === requestedAnalysisJobId)) return;
    handledAnalysisRequestRef.current = requestKey;
    openAnalysis(requestedAnalysisJobId);
  }, [requestedAnalysisJobId, analysisActivationKey, availableAnalysisJobs, openAnalysis]);

  useEffect(() => {
    if (activeAnalysisJobId && !availableAnalysisJobs.some((job) => job.id === activeAnalysisJobId)) {
      setActiveAnalysisJobId("");
    }
  }, [activeAnalysisJobId, availableAnalysisJobs]);

  useEffect(() => {
    const requestKey = `${requestedActiveId}:${activationKey}`;
    if (!requestedActiveId || handledPresentationRequestRef.current === requestKey) return;
    if (!presentations.some((item) => item.id === requestedActiveId)) return;
    handledPresentationRequestRef.current = requestKey;
    activate(requestedActiveId, true, true);
  }, [requestedActiveId, activationKey, presentations, activate]);

  useEffect(() => {
    if (!presentationsHydrated) return;
    if (hydratedPresentationScopeRef.current !== presentationScopeKey) {
      handledReadyRef.current = new Set(
        presentations.filter((item) => item.status === "ready").map((item) => item.id)
      );
      hydratedPresentationScopeRef.current = presentationScopeKey;
      return;
    }
    const newlyReady = presentations.find((item) =>
      item.status === "ready" && item.autoOpen !== false && !handledReadyRef.current.has(item.id)
    );
    presentations.forEach((item) => {
      if (item.status === "ready") handledReadyRef.current.add(item.id);
    });
    if (newlyReady && !keepSelectedResult) activate(newlyReady.id);
  }, [presentations, presentationsHydrated, presentationScopeKey, activate, keepSelectedResult]);

  useEffect(() => {
    if (!showResultsDock) return undefined;
    const manifest = activePresentationSnapshot;
    if (!manifest) {
      setPresentation(null);
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      const detail = manifest;
      let nextRows = Array.isArray(detail.rows) ? detail.rows : Array.isArray(detail.segments) ? detail.segments : [];
      const storagePath = detail.storagePath || detail.data?.storagePath;
      if (storagePath) {
        const payload = await fetchJsonFromStorage(storagePath);
        if (!payload) throw new Error("Could not load presentation rows from Cloud Storage.");
        nextRows = Array.isArray(payload) ? payload : Array.isArray(payload.rows) ? payload.rows : Array.isArray(payload.segments) ? payload.segments : [];
      }
      if (cancelled) return;
      setPresentation(detail);
      setRows(nextRows);
      onActivate?.(detail, nextRows);
    })().catch((reason) => {
      if (!cancelled) setError(String(reason?.message || reason));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [activePresentationDataKey, onActivate, showResultsDock]);

  useEffect(() => {
    if (!activePresentationSnapshot) return;
    setPresentation((current) => current?.id === activePresentationSnapshot.id ? activePresentationSnapshot : current);
  }, [activePresentationSnapshot]);

  const startResize = useCallback((event) => {
    if (!open) return;
    event.preventDefault();
    const shell = shellRef.current;
    if (!shell) return;
    setResizing(true);
    const resize = (moveEvent) => {
      const bounds = shell.getBoundingClientRect();
      const next = ((moveEvent.clientY - bounds.top) / bounds.height) * 100;
      setResultsPercent(Math.max(22, Math.min(78, next)));
    };
    const stop = () => {
      setResizing(false);
      window.removeEventListener("pointermove", resize);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", resize);
    window.addEventListener("pointerup", stop);
  }, [open]);

  const columns = useMemo(() => normalizeColumns(presentation, rows), [presentation, rows]);
  const binding = presentation?.entityBinding;
  const isBoxWhisker = presentation?.kind === "box_whisker";
  const isXYChart = presentation?.kind === "chart";
  const isTemporal = presentation?.kind === "timeline_map" && presentation?.id === activeId;
  const temporalResultId = String(presentation?.network?.resultId || "").replace(/^\/+|\/+$/g, "");
  const temporalSegments = useMemo(() => isTemporal ? normalizeTemporalSegments(rows) : [], [isTemporal, rows]);
  const loadTemporalHazard = useCallback((resultId, time) => {
    const cache = temporalHazardCacheRef.current;
    const key = `${resultId}:${time}`;
    if (cache.has(key)) return cache.get(key).promise;
    const entry = { resultId, time, polygons: null, promise: null };
    entry.promise = fetchJsonFromStorage(`${resultId}/fire_inputs/fire_${time}.geojson`)
      .then(hazardPolygonsFromGeojson).catch(() => []).then((polygons) => {
        entry.polygons = polygons;
        setTemporalHazardCacheVersion((version) => version + 1);
        return polygons;
      });
    cache.set(key, entry);
    if (cache.size > 64) cache.delete(cache.keys().next().value);
    return entry.promise;
  }, []);
  useEffect(() => {
    setShowTemporalHazard(false);
  }, [activeId]);
  useEffect(() => {
    if (!isTemporal || !showTemporalHazard || !temporalResultId) return undefined;
    let cancelled = false;
    setHazardSnapshotIndex({ resultId: temporalResultId, status: "loading", times: [] });
    listAll(storageRef(storage, `${temporalResultId}/fire_inputs`)).then((listing) => {
      if (cancelled) return;
      const times = listing.items.map((item) => /^fire_(\d+)\.geojson$/.exec(item.name))
        .filter(Boolean).map((match) => Number(match[1])).sort((a, b) => a - b);
      setHazardSnapshotIndex({ resultId: temporalResultId, status: "ready", times });
    }).catch(() => {
      if (!cancelled) setHazardSnapshotIndex({ resultId: temporalResultId, status: "unavailable", times: [] });
    });
    return () => { cancelled = true; };
  }, [isTemporal, showTemporalHazard, temporalResultId]);
  const hazardTimes = hazardSnapshotIndex.resultId === temporalResultId ? hazardSnapshotIndex.times : EMPTY_HAZARD_TIMES;
  // Path event times need not match the fire simulation's saved snapshot times.
  const hazardSnapshotTime = [...hazardTimes].reverse().find((time) => time <= temporalTime);
  useEffect(() => {
    if (!isTemporal || !showTemporalHazard || !temporalResultId) return;
    if (hazardSnapshotTime !== undefined) loadTemporalHazard(temporalResultId, hazardSnapshotTime);
    hazardTimes.filter((time) => time > temporalTime).slice(0, 8)
      .forEach((time) => loadTemporalHazard(temporalResultId, time));
  }, [isTemporal, showTemporalHazard, temporalResultId, temporalTime, hazardSnapshotTime, hazardTimes, loadTemporalHazard]);
  const temporalHazardDisplay = (() => {
    const entries = [...temporalHazardCacheRef.current.values()]
      .filter((entry) => entry.resultId === temporalResultId && entry.time <= temporalTime);
    const current = entries.find((entry) => entry.time === hazardSnapshotTime);
    const latestLoaded = entries.filter((entry) => entry.polygons?.length)
      .sort((a, b) => b.time - a.time)[0];
    return {
      polygons: current?.polygons?.length ? current.polygons : latestLoaded?.polygons || [],
      ready: hazardSnapshotIndex.resultId === temporalResultId && hazardSnapshotIndex.status !== "loading"
        && (hazardSnapshotTime === undefined || (current?.polygons !== null && current?.polygons !== undefined) || !!latestLoaded),
    };
  })();
  const magnitudeRanges = useMemo(() => temporalMagnitudeRanges(temporalSegments, temporalColors, temporalBoundaries), [temporalSegments, temporalColors, temporalBoundaries]);
  const magnitudeColor = useCallback((magnitude) => magnitude == null || !magnitudeRanges.length ? null
    : (magnitudeRanges.find((range) => magnitude <= range.max) || magnitudeRanges[magnitudeRanges.length - 1]).color, [magnitudeRanges]);
  useEffect(() => {
    setTemporalColors(DEFAULT_MAP_COLORS);
    setTemporalBoundaries(null);
  }, [activeId]);
  const temporalNetwork = presentation?.network || EMPTY_NETWORK;
  useEffect(() => {
    if (!isTemporal) return undefined;
    let cancelled = false;
    setTemporalGeometry({ presentationId: "", links: [], nodes: [], missingGeometry: 0 });
    resolveTemporalGeometry(temporalSegments, temporalNetwork).then((geometry) => {
      if (!cancelled) setTemporalGeometry({ presentationId: activeId, ...geometry });
    });
    return () => { cancelled = true; };
  }, [activeId, isTemporal, temporalSegments, temporalNetwork]);
  const temporalOverlay = useMemo(() => {
    if (!isTemporal || temporalGeometry.presentationId !== activeId) return null;
    const active = activeTemporalSegments(temporalSegments, temporalTime, temporalBounds(temporalSegments).max);
    return {
      presentationId: activeId,
      ...temporalGeometry,
      activeLinks: active.filter((segment) => segment.entityType === "link").map((segment) => ({ id: segment.entityId, color: magnitudeColor(segment.magnitude) })),
      activeNodes: active.filter((segment) => segment.entityType === "node").map((segment) => ({ id: segment.entityId, color: magnitudeColor(segment.magnitude) })),
    };
  }, [activeId, isTemporal, temporalGeometry, temporalSegments, temporalTime, magnitudeColor]);
  const selectTemporalSegment = useCallback((segment) => onSelectTemporalEntity?.(presentation, segment), [onSelectTemporalEntity, presentation]);
  const selectTemporalMapEntity = useCallback((entityType, entityId) => onSelectTemporalEntity?.(presentation, { entityType, entityId }), [onSelectTemporalEntity, presentation]);
  const scrollToMap = useCallback(() => {
    const shell = shellRef.current;
    const map = mapRef.current;
    if (!shell || !map) return;
    shell.scrollTo({ top: shell.scrollTop + map.getBoundingClientRect().top - shell.getBoundingClientRect().top, behavior: "smooth" });
  }, []);
  const scrollToResults = useCallback(() => {
    shellRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const updateJumpTarget = useCallback(() => {
    const shell = shellRef.current;
    const map = mapRef.current;
    if (!showResultsDock || !open || !shell || !map) {
      setJumpTarget(null);
      return;
    }
    const mapTop = map.getBoundingClientRect().top - shell.getBoundingClientRect().top;
    setJumpTarget(mapTop > shell.parentElement.clientHeight / 2 ? "map" : "results");
  }, [open, showResultsDock]);
  useEffect(() => {
    updateJumpTarget();
    const shell = shellRef.current;
    const map = mapRef.current;
    if (!shell || !map || !open || !showResultsDock) return undefined;
    const observer = new ResizeObserver(updateJumpTarget);
    observer.observe(shell);
    observer.observe(map);
    const dock = shell.querySelector(".agent-resultsDock");
    if (dock) observer.observe(dock);
    window.addEventListener("resize", updateJumpTarget);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateJumpTarget);
    };
  }, [open, showResultsDock, resultsPercent, updateJumpTarget]);
  const isBound = presentation?.kind === "table" && isDrawableBinding(binding);
  const mapMetrics = useMemo(
    () => isBound ? numericMetricKeys(columns, rows, binding.idKey) : [],
    [isBound, columns, rows, binding?.idKey]
  );
  const mapMetricKey = mapMetrics.join("|");
  useEffect(() => {
    if (!isBound) {
      setMapMetric("");
      setMapColors(DEFAULT_MAP_COLORS);
      setMapBoundaries(null);
      return;
    }
    const recommendedMetric = presentation?.mapStyle?.metricKey;
    setMapMetric(mapMetrics.includes(recommendedMetric) ? recommendedMetric : mapMetrics[0] || "");
    const recommendedColors = presentation?.mapStyle?.ranges?.map((range) => range.color).filter(Boolean);
    setMapColors(recommendedColors?.length === DEFAULT_MAP_COLORS.length ? recommendedColors : DEFAULT_MAP_COLORS);
    setMapBoundaries(null);
  }, [activeId, isBound, mapMetricKey, presentation?.mapStyle, mapMetrics]);
  const colorRanges = useMemo(() => mapRanges(rows, mapMetric, mapColors, mapBoundaries), [rows, mapMetric, mapColors, mapBoundaries]);
  const changeMapBoundary = useCallback((value, index) => {
    setMapBoundaries(colorRanges.slice(0, -1).map((range, rangeIndex) => rangeIndex === index ? value : range.max));
  }, [colorRanges]);
  const changeMapColor = useCallback((color, index) => {
    setMapColors((current) => current.map((item, itemIndex) => itemIndex === index ? color : item));
  }, []);
  const resetMapScale = useCallback(() => {
    setMapColors(DEFAULT_MAP_COLORS);
    setMapBoundaries(null);
  }, []);
  const selectedIds = selectedIdsByType?.[binding?.type] || EMPTY_SELECTION;
  const sortedRows = useMemo(() => {
    if (!isBound || !selectedIds.size) return rows;
    return [...rows].sort((a, b) => Number(selectedIds.has(String(b?.[binding.idKey] ?? ""))) - Number(selectedIds.has(String(a?.[binding.idKey] ?? ""))));
  }, [binding?.idKey, isBound, rows, selectedIds]);
  useEffect(() => {
    if (!showResultsDock || !isBound || !mapMetric || !colorRanges.length) {
      onMapOverlayChange?.(null);
      return;
    }
    // Every row's value is published, and the map decides which of them to paint from the current
    // selection. Keeping the set stable means the map's overlay shapes are not torn down and rebuilt
    // each time a row is toggled.
    const values = {};
    rows.forEach((row) => {
      const id = String(row?.[binding.idKey] ?? "").trim();
      const value = Number(row?.[mapMetric]);
      if (id && Number.isFinite(value)) values[id] = value;
    });
    onMapOverlayChange?.({ presentationId: activeId, binding, metricKey: mapMetric, metricLabel: columns.find((column) => column.key === mapMetric)?.label || mapMetric, ranges: colorRanges, values });
  }, [activeId, binding, colorRanges, columns, isBound, mapMetric, onMapOverlayChange, rows, showResultsDock]);
  const boundIds = isBound
    ? rows.map((row) => String(row?.[binding.idKey] ?? "").trim()).filter(Boolean)
    : [];
  const allBoundSelected = boundIds.length > 0 && boundIds.every((id) => selectedIds.has(id));
  const someBoundSelected = boundIds.some((id) => selectedIds.has(id));
  const downloadCsv = () => downloadPresentationCsv({
    rows: isTemporal ? temporalSegments : rows,
    fields: isTemporal
      ? ["entityType", "entityId", "startS", "endS", "magnitude", "label"]
      : isBoxWhisker
      ? boxWhiskerFieldKeys(presentation?.chart)
      : isXYChart
        ? xyChartFieldKeys(presentation?.chart)
      : columns.map((column) => column.key),
    filename: presentation?.title || activeId,
  });

  return (
    <div className="agent-resultsWorkspace">
      {jumpTarget === "results" && <div className="agent-resultsJumpBar"><ScrollJumpButton direction="up" destination="analysis" onClick={scrollToResults} /></div>}
      <div className="agent-resultsScroll" ref={shellRef} onScroll={updateJumpTarget}>
      {showResultsDock && <div className={`agent-resultsDock ${resizing ? "isResizing" : ""}`} style={{ flexBasis: open ? `${resultsPercent}%` : "44px" }}>
        <div className="agent-resultsBar">
          <div className="agent-resultsChips">
            {analysisChips.map((job) => <button key={job.id} type="button" className={activeAnalysisJob?.id === job.id ? "isActive" : ""} onClick={() => openAnalysis(job.id)}>
              {job.status === "running" ? "● " : ""}{job.id === latestAnalysisJob?.id ? "Live analysis" : "Analysis"}
            </button>)}
            {visiblePresentations.map((item) => (
              <button key={item.id} type="button" className={item.id === activeId ? "isActive" : ""} onClick={() => activate(item.id, true, true)}>
                {item.status === "preparing" ? "● " : ""}{item.title || item.id}
              </button>
            ))}
          </div>
          <div className="agent-resultsActions">
            {!!presentations.length && <div className="agent-resultsHistoryWrap">
              <button type="button" onClick={() => setHistoryOpen((value) => !value)}>All results ▾</button>
              {historyOpen && <div className="agent-resultsHistory">
                {[...allPresentations].reverse().map((item) => <button key={item.id} type="button" className={item.id === activeId ? "isActive" : ""} onClick={() => activate(item.id, true, true)}><strong>{item.title || item.id}</strong><small>{item.status || item.kind || "result"}</small></button>)}
              </div>}
            </div>}
            {(activeId || activeAnalysisJob) && !open && <button type="button" onClick={() => {
              if (activeAnalysisJob) {
                setResultsPercent(MAX_RESULTS_PERCENT);
                setOpen(true);
                return;
              }
              if (["table", "box_whisker", "chart", "timeline_map"].includes(activePresentation?.kind)) {
                setResultsPercent(MAX_RESULTS_PERCENT);
              }
              setOpen(true);
            }}>Open</button>}
            {open && <button className="agent-resultsClose" type="button" aria-label="Close results" title="Close results" onClick={() => {
              onClose?.(activeAnalysisJobId || [...availableAnalysisJobs]
                .filter((job) => !TERMINAL_JOB_STATUSES.has(job.status))
                .sort((a, b) => analysisJobUpdatedAt(b) - analysisJobUpdatedAt(a))[0]?.id || "");
              setOpen(false);
              setActiveAnalysisJobId("");
            }}>×</button>}
          </div>
        </div>
        {open && (
          <section className="agent-resultsPanel">
            {activeAnalysisJobId ? (activeAnalysisJob ? <LiveAnalysisWorkspace job={activeAnalysisJob} requestedTab={activeAnalysisJobId === requestedAnalysisJobId ? requestedAnalysisTab : ""} tabActivationKey={analysisActivationKey} /> : <div className="agent-resultsEmpty">Loading analysis…</div>) : loading ? <div className="agent-resultsEmpty">Loading results…</div> : error ? <div className="agent-resultsError">{error}</div> : activeId && presentation?.id !== activeId ? <div className="agent-resultsEmpty">Loading results…</div> : activeId && presentation ? (
              <>
                <header><div><h3>{presentation.title || activeId}</h3>{presentation.subtitle && <p>{presentation.subtitle}</p>}</div><div className="agent-resultsHeaderActions"><span>{(isTemporal ? temporalSegments.length : rows.length).toLocaleString()} {isTemporal ? "steps" : isBoxWhisker ? "distributions" : isXYChart ? "points" : "rows"}</span><button type="button" disabled={isTemporal ? !temporalSegments.length : !rows.length} onClick={downloadCsv} aria-label="Download CSV" title="Download CSV"><Download size={15} /></button></div></header>
                {isBound && <div className="agent-boundToolbar">
                  {!!mapMetric && <div className="agent-mapScaleEditor">
                    <ColourBar
                      scale={{ ranges: colorRanges, min: colorRanges[0]?.min, max: colorRanges[colorRanges.length - 1]?.max }}
                      label="Range and colors"
                      onBoundaryChange={changeMapBoundary}
                      onColorChange={changeMapColor}
                      onReset={resetMapScale}
                      boundaryStep={0}
                    />
                  </div>}
                  {!!mapMetrics.length && <label>Color map by <select value={mapMetric} onChange={(event) => {
                    setMapMetric(event.target.value);
                    setMapBoundaries(null);
                  }}><option value="">None</option>{mapMetrics.map((key) => <option key={key} value={key}>{columns.find((column) => column.key === key)?.label || key}</option>)}</select></label>}
                </div>}
                {isTemporal ? <AgentTemporalTimeline key={activeId} segments={temporalSegments} time={temporalTime} onTimeChange={setTemporalTime} onPlay={scrollToMap} canAdvance={!showTemporalHazard || temporalHazardDisplay.ready} onSelectSegment={selectTemporalSegment} missingGeometry={temporalGeometry.presentationId === activeId ? temporalGeometry.missingGeometry : 0} magnitudeRanges={magnitudeRanges} magnitudeColor={magnitudeColor} magnitudeLabel={presentation.magnitudeLabel || "Magnitude"} onMagnitudeBoundaryChange={(value, index) => setTemporalBoundaries((current) => { const next = [...(current || magnitudeRanges.slice(0, -1).map((range) => range.max))]; next[index] = value; return next; })} onMagnitudeColorChange={(color, index) => setTemporalColors((current) => current.map((item, itemIndex) => itemIndex === index ? color : item))} onMagnitudeReset={() => { setTemporalColors(DEFAULT_MAP_COLORS); setTemporalBoundaries(null); }} /> : isBoxWhisker ? <AgentBoxWhiskerChart rows={rows} config={presentation.chart} /> : isXYChart ? <AgentXYChart rows={rows} config={presentation.chart} /> : <div className="agent-resultsTableWrap">
                  <table><thead><tr>{isBound && <th className="agent-boundActionsCol"><div><input type="checkbox" checked={allBoundSelected} ref={(input) => { if (input) input.indeterminate = someBoundSelected && !allBoundSelected; }} aria-label={allBoundSelected ? "Clear table selection" : "Select all table rows"} onChange={() => onSetBoundSelection?.(binding, allBoundSelected ? [] : boundIds)} /><button type="button" disabled={!boundIds.length} title="Zoom to all" aria-label="Zoom to all table entities" onClick={() => { onZoomBoundEntities?.(binding, boundIds); scrollToMap(); }}><Search size={15} /></button></div></th>}{columns.map((column) => <th key={column.key}>{column.label || column.key}</th>)}</tr></thead>
                    <tbody>{sortedRows.map((row, rowIndex) => {
                      const entityId = isBound ? String(row?.[binding.idKey] ?? "").trim() : "";
                      const isSelected = !!entityId && selectedIds.has(entityId);
                      return <tr key={row.id || rowIndex} className={isSelected ? "isSelected" : ""} onClick={isBound && entityId ? () => onToggleBoundEntity?.(binding, entityId) : undefined}>
                        {isBound && <td className="agent-boundActionsCol"><div><input type="checkbox" checked={isSelected} readOnly disabled={!entityId} /><button type="button" disabled={!entityId} title="Zoom to entity" aria-label={`Zoom to ${entityId || "entity"}`} onClick={(event) => { event.stopPropagation(); onZoomBoundEntities?.(binding, [entityId]); scrollToMap(); }}><Search size={15} /></button></div></td>}
                        {columns.map((column) => <td key={column.key}>{formatCell(row[column.key], column.type)}</td>)}
                      </tr>;
                    })}</tbody>
                  </table>
                </div>}
              </>
            ) : <div className="agent-resultsEmpty">Select a result to view it.</div>}
          </section>
        )}
        {open && <div className="agent-resultsDivider" role="separator" aria-orientation="horizontal" onPointerDown={startResize}><span /></div>}
      </div>}
      <div className={`agent-resultsMap${React.Children.toArray(children).some((child) => React.isValidElement(child) && child.props.simulationId && child.props.isVisible) ? " isSimulationResults" : ""}`} ref={mapRef}>
        {React.Children.map(children, (child) => React.isValidElement(child) ? child.props.simulationId ? child : React.cloneElement(child, {
          temporalOverlay: showResultsDock ? temporalOverlay : null,
          temporalHazardAvailable: showResultsDock && isTemporal && !activeAnalysisJob && !!temporalResultId,
          temporalHazardEnabled: showTemporalHazard,
          onTemporalHazardChange: setShowTemporalHazard,
          temporalHazardPolygons: showResultsDock && showTemporalHazard && isTemporal && !activeAnalysisJob ? temporalHazardDisplay.polygons : [],
          onTemporalSelect: selectTemporalMapEntity,
          returnToSimulationResults,
          onReturnToSimulationResults,
        }) : child)}
      </div>
      </div>
      {jumpTarget === "map" && <ScrollJumpButton className="agent-resultsJump--map" direction="down" destination="map" onClick={scrollToMap} />}
    </div>
  );
}
